/**
 * The man in a turret bunker (02 §9.2): "360°, slow traverse (45°/s) - outrun the barrel".
 * He sees all round through his slits, but the gun only fires once the turret has come round
 * to the target, and the turret is slow. Everything else about him is an ordinary manned
 * emplacement: a soldier with `mans` set to a `turretBunker` prop.
 *
 * `facing` is the barrel; the renderer should turn the turret to it.
 */

import type { Ctx } from '../../ctx';
import { angleDiff, atan2, PI } from '../../math';
import { ENEMY, secs, SPECIALS, TACTICS } from '../../tuning';
import type { Enemy } from '../../types';
import { enterCombat } from '../fight';
import { turnToward } from '../movement';
import { perceive } from '../perception';
import { shootAtRanger } from '../shooting';

const DEG = PI / 180;

export function mansTurretBunker(ctx: Ctx, e: Enemy): boolean {
  return e.mans !== null && ctx.statics.def.props.find((p) => p.id === e.mans)?.kind === 'turretBunker';
}

export function stepTurretGunner(ctx: Ctx, e: Enemy): void {
  const w = ctx.world;
  const r = w.ranger;
  const bearing = atan2(r.x - e.x, r.z - e.z);
  e.moving = false;
  if ((w.tick + e.thinkSlot) % ENEMY.perceptionEveryTicks === 0) {
    // All-round vision: he looks the ranger's way for the perception pass (so every rule in it -
    // range, trenches, water, line of sight - applies to him unchanged), then the barrel is put back.
    const barrel = e.facing;
    e.facing = bearing;
    perceive(ctx, e);
    e.facing = barrel;
  }
  if (e.detection >= 1 && e.seesRanger) enterCombat(ctx, e);

  const traverse = SPECIALS.turretTraverseDeg * DEG;
  if (e.state !== 'combat') {
    turnToward(e, e.homeFacing, traverse);
    return;
  }
  if (!e.seesRanger) {
    if (e.lostTicks > secs(4)) {
      e.state = 'guard';
      e.detection = 0.3;
    }
    return;
  }
  turnToward(e, bearing, traverse);
  const helpless = r.status === 'downed' && r.sinceShot > TACTICS.holdFireAfterTicks;
  const bears = Math.abs(angleDiff(e.facing, bearing)) <= SPECIALS.turretAimToleranceDeg * DEG;
  if (e.reactTicks > 0) e.reactTicks--;
  else if (!helpless && bears) shootAtRanger(ctx, e);
}
